import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Snowflake, Clock, AlertCircle } from 'lucide-react';

const AnimatedSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const Schedule = () => {
  // Timings are unchanged. Every hour slot runs 3 classes at once:
  // one Striking + one Grappling + one Fitness. The specific discipline is
  // fixed per day (below), so members know exactly what's on each day.
  const timeNote = 'Morning 06:00–10:00 · Evening 18:00–21:00';

  const weekdays = [
    { day: 'Monday', striking: 'Kickboxing', grappling: 'BJJ' },
    { day: 'Tuesday', striking: 'Boxing', grappling: 'Wrestling' },
    { day: 'Wednesday', striking: 'Muay Thai', grappling: 'MMA' },
    { day: 'Thursday', striking: 'Boxing', grappling: 'Wrestling' },
    { day: 'Friday', striking: 'Kickboxing', grappling: 'BJJ' },
  ];

  const saturday = {
    striking: 'Kickboxing',
    grappling: 'BJJ — Open Mat',
    timesNote: 'Morning 07:00–10:00 · Evening 18:00–21:00',
    special: 'Open Mat 09:00–10:00 · Sparring 19:00–20:00',
  };

  // Two Pro classes (approval required)
  const proClasses = [
    { time: '11:00 – 13:00', label: 'Afternoon Pro Session' },
    { time: '21:00 – 22:00', label: 'Evening Pro Session' },
  ];

  const Dot = ({ color }) => <span className={`w-2.5 h-2.5 rounded-full ${color} shrink-0`} />;

  const cardClass = 'bg-cobra-dark/50 backdrop-blur-sm border border-white/10 rounded-sm p-6 hover:border-cobra-red/40 transition-colors';

  const DayCard = ({ day, striking, grappling }) => (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-anton text-2xl text-white uppercase tracking-wider">{day}</h3>
        <span className="w-8 h-1 bg-cobra-red rounded-full" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Dot color="bg-red-500" />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Striking</p>
            <p className="text-white font-manrope">{striking}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Dot color="bg-blue-500" />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Grappling</p>
            <p className="text-white font-manrope">{grappling}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Dot color="bg-green-500" />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Fitness</p>
            <p className="text-white font-manrope">Fitness Circuit</p>
          </div>
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-white/60 text-xs font-manrope">
        <Clock size={14} className="text-cobra-red" />
        {timeNote}
      </div>
    </div>
  );

  return (
    <div className="bg-cobra-black min-h-screen">
      {/* Hero */}
      <section
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        data-testid="schedule-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/4162451/pexels-photo-4162451.jpeg?auto=compress&cs=tinysrgb&w=1280)',
          }}
        />
        <div className="absolute inset-0 bg-cobra-black/85" />
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 font-anton text-5xl md:text-7xl lg:text-8xl text-white uppercase tracking-wider text-center px-4"
          data-testid="schedule-title"
        >
          CLASS <span className="text-cobra-red text-shadow-glow">SCHEDULE</span>
        </motion.h1>
      </section>

      {/* Schedule Overview */}
      <section className="py-20 md:py-32 px-4 md:px-8 lg:px-12" data-testid="schedule-section">
        <div className="max-w-6xl mx-auto">

          {/* Weekly Schedule (all 7 days) */}
          <AnimatedSection>
            <h2 className="font-anton text-3xl md:text-4xl text-white uppercase tracking-wider mb-2">
              Weekly Schedule
            </h2>
            <div className="h-1 w-24 bg-cobra-red mb-4" />
            <p className="text-white/60 font-manrope max-w-3xl mb-8">
              Three classes run at the same time in every hour slot — one <span className="text-red-400 font-semibold">Striking</span>,
              one <span className="text-blue-400 font-semibold">Grappling</span> and one <span className="text-green-400 font-semibold">Fitness</span>.
              Here's exactly which discipline is on each day.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-testid="weekday-grid">
              {weekdays.map((d) => (
                <DayCard key={d.day} {...d} />
              ))}

              {/* Saturday */}
              <div className={cardClass} data-testid="saturday-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-anton text-2xl text-white uppercase tracking-wider">Saturday</h3>
                  <span className="w-8 h-1 bg-cobra-red rounded-full" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Dot color="bg-red-500" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Striking</p>
                      <p className="text-white font-manrope">{saturday.striking}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Dot color="bg-blue-500" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Grappling</p>
                      <p className="text-white font-manrope">{saturday.grappling}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Dot color="bg-green-500" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Fitness</p>
                      <p className="text-white font-manrope">Fitness Circuit</p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-white/10 space-y-2">
                  <div className="flex items-center gap-2 text-white/60 text-xs font-manrope">
                    <Clock size={14} className="text-cobra-red" />
                    {saturday.timesNote}
                  </div>
                  <p className="text-cobra-red/80 text-xs font-manrope">{saturday.special}</p>
                </div>
              </div>

              {/* Sunday */}
              <div className={cardClass} data-testid="sunday-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-anton text-2xl text-white uppercase tracking-wider">Sunday</h3>
                  <span className="w-8 h-1 bg-cyan-400 rounded-full" />
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <Clock size={18} className="text-white/60" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Open Hours</p>
                    <p className="text-white font-manrope font-semibold">09:00 – 12:00</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <Snowflake size={18} className="text-cyan-400" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/40 font-manrope">Ice Baths</p>
                    <p className="text-cyan-400 font-manrope font-semibold">09:00 – 12:00</p>
                  </div>
                </div>
                <p className="text-white/40 font-manrope text-xs italic mt-4">Open unless a special event is announced.</p>
              </div>

              {/* Pro Classes — sits to the right of Sunday, always visible */}
              <div className="bg-cobra-red/5 border border-cobra-red/30 rounded-sm p-6 lg:col-span-2" data-testid="pro-classes-card">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-anton text-2xl text-cobra-red uppercase tracking-wider flex items-center gap-2">
                    <AlertCircle size={20} />
                    Pro Classes
                  </h3>
                  <span className="bg-cobra-red text-white px-3 py-1 rounded-sm font-bold text-xs uppercase">Mon – Fri</span>
                </div>
                <p className="text-white/60 font-manrope text-sm mb-5">
                  Advanced sessions for competitive fighters — approval from the coaching staff required.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {proClasses.map((p) => (
                    <div key={p.time} className="flex items-center justify-between bg-cobra-dark/50 border border-white/10 rounded-sm px-5 py-4">
                      <div>
                        <div className="font-bebas text-2xl text-cobra-red">{p.time}</div>
                        <p className="text-white/70 font-manrope text-sm">{p.label}</p>
                      </div>
                      <span className="bg-cobra-red text-white px-3 py-1.5 rounded-sm font-bold text-xs uppercase whitespace-nowrap">Pro Only</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Legend */}
          <AnimatedSection delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-cobra-dark/30 border border-white/10 rounded-sm p-6">
                <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-4">
                  Class Types
                </h3>
                <ul className="space-y-3 font-manrope text-sm text-white/70">
                  <li className="flex items-center gap-3">
                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                    <span><strong className="text-white">Striking</strong> - Muay Thai, Boxing, Kickboxing</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    <span><strong className="text-white">Grappling</strong> - BJJ, Wrestling, MMA</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    <span><strong className="text-white">Fitness</strong> - Circuit-based training (changes daily)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-cobra-dark/30 border border-white/10 rounded-sm p-6">
                <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-4">
                  Important Notes
                </h3>
                <ul className="space-y-2 font-manrope text-sm text-white/70">
                  <li>• 3 classes run simultaneously every hour (Striking · Grappling · Fitness)</li>
                  <li>• Morning 06:00–10:00 · Evening 18:00–21:00 (Mon-Fri)</li>
                  <li>• Pro Classes 11:00–13:00 &amp; 21:00–22:00 (approval required)</li>
                  <li>• Ice baths: Sunday only, 09:00–12:00</li>
                </ul>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Schedule;
